import { Box } from '@mui/material';
import { FC } from 'react';
import { useGetIdentity, useGetOne, useTranslate } from 'react-admin';

import { GetDistributorDashboardOutstandingBreakdownEndpoint } from '@Common/types/bo_external/distributor/dashboard';

import { ShowSection } from '@sh4res/bo-common/components';
import { CurrencyField } from '@sh4res/bo-common/fields';

const breakdownStyle = {
  display: 'grid',
  gridTemplateColumns: '34% 66%',
  borderTop: '1px solid #E8E8E8',
  padding: '15px',
};

export const OutstandingBreakdown: FC<{ source: 'standard' | 'er' }> = ({ source }) => {
  const testId = 'BoExternalOutstandingBreakdown';
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();

  const { data } = useGetOne<
    GetDistributorDashboardOutstandingBreakdownEndpoint['Client']['ResponseBody']
  >(
    source === 'standard'
      ? 'distributor/dashboard/outstanding_breakdown'
      : 'distributor/dashboard/outstanding_breakdown/er',
    {
      id: identity?.id as number,
    },
    {
      enabled: !!identity,
    },
  );

  if (!data) {
    return;
  }

  return (
    <ShowSection label="resources.distributor.dashboard.outstanding_breakdown.title" noCard={true}>
      <ShowSection
        component="ul"
        label="resources.distributor.dashboard.outstanding_breakdown.contracts"
        sx={{ margin: '15px' }}>
        {data.contracts.map((contract, idx) => (
          <Box key={idx} sx={breakdownStyle}>
            <Box>
              {`${translate(`resources.bo_common.b2b.contracts.choices.contract_type.${contract.contract_type}`)} ${contract.contract_number ? `(n° ${contract.contract_number})` : ''}`}
            </Box>
            <CurrencyField
              source="amount"
              record={contract}
              currencyDisplay="symbol"
              sx={{ fontSize: '14px', margin: 0 }}
              data-testid={`${testId}_ContractAmountField`}
            />
          </Box>
        ))}
      </ShowSection>
      <ShowSection
        component="ul"
        label="resources.distributor.dashboard.outstanding_breakdown.plans"
        sx={{ margin: '15px' }}>
        {data.plans.map((plan, idx) => (
          <Box key={idx} sx={breakdownStyle}>
            <Box>
              {translate(
                `resources.bo_common.b2b.companies.saving_plans.types_short.${plan.category}`,
              )}
            </Box>
            <CurrencyField
              source="amount"
              record={plan}
              currencyDisplay="symbol"
              sx={{ fontSize: '14px', margin: 0 }}
              data-testid={`${testId}_PlanAmountField`}
            />
          </Box>
        ))}
      </ShowSection>
      <ShowSection
        component="ul"
        label="resources.distributor.dashboard.outstanding_breakdown.instruments"
        sx={{ margin: '15px' }}>
        {data.instruments.map((instrument, idx) => (
          <Box key={idx} sx={breakdownStyle}>
            <Box>{instrument.name}</Box>
            <CurrencyField
              source="amount"
              record={instrument}
              currencyDisplay="symbol"
              sx={{ fontSize: '14px', margin: 0 }}
              data-testid={`${testId}_InstrumentAmountField`}
            />
          </Box>
        ))}
      </ShowSection>
    </ShowSection>
  );
};
